import { currencySymbol, postcodeWords, roundConfig } from '../round-config'
import { api, cancelQueued, dismiss, getQueue, mutate, onQueueChange } from '../api'
import { routeGen } from '../route-state'
import { clear, el, loadError, money as moneyWithPence, parseMoney } from '../ui'
import {
  blankPriceList,
  HOW_OFTEN,
  PRICE_LIST_SCHEMA,
  quote,
  sizeRates,
  type HowOften,
  type Job,
  type JobKind,
  type PriceList,
  type PriceMethod,
  oftenPct,
} from '../../functions/lib/price-list'
import { kindLabel } from '../trade'
import { getMe } from '../me'

// By size in square metres (Liam picked A, 15 Oct 2026): a rate for each kind of job, in the round's own words.
const KINDS: JobKind[] = ['window', 'gutter', 'other']
const pricedKinds = (list: PriceList): JobKind[] => KINDS.filter((k) => (sizeRates(list)[k] ?? 0) > 0)

// The area price box, in the round's country's words (Step 2 round 3).
const AREA_HINT: Record<'GB' | 'US' | 'CA', { label: string; example: string }> = {
  GB: { label: 'Postcodes starting', example: 'N10, N8' },
  US: { label: 'ZIP codes', example: '10001, 10002' },
  CA: { label: 'Postal codes starting', example: 'M5V, M4C' },
}

// "Your prices" (owner, 0037, 2026-10-01; plan docs/plans/next-level/plan.html step 1 of quotes by email).
//
// First time: one question at a time (Liam, 1 Oct: "make this screens and onboarding cleaner and easier to use,
// maybe like we did with glow animations"; mockup docs/plans/next-level/prices-onboarding-mockup.html, which he
// OK'd: "much better"). Most answers are one tap; the Next button glows like the first-booking guide; "Your prices
// so far" and an example job's price fill in beside the card; a few jobs are priced for a last check; a tick pops
// on save. After that: a short list of what was set, each line with Change, which opens just that one question.
//
// Every price shown comes from quote() in functions/lib/price-list.ts, the very sum the quote drafts will use.
// No link to this page anywhere (Liam, 1 Oct): owners will arrive here by switching quotes on.
// Laptop first (Liam, 24 Sep: owner pages are designed for the laptop); one column on a phone.

type StepName = 'how' | 'price' | 'areas' | 'first' | 'often' | 'extras' | 'least' | 'check'
type Mode = 'setup' | 'summary' | 'change' | 'done'

interface PricesPayload {
  list: PriceList | null
  version: number
}

let payload: PricesPayload | null = null
let draft: PriceList | null = null // what is on screen
let saved: PriceList | null = null // what the server has
let mode: Mode = 'setup'
let stepIndex = 0
let changing: StepName | null = null // summary > Change: the one question open
let answered = new Set<StepName>() // first time: questions with an answer (Next waits for one)
let areasOn: boolean | null = null
let justSaved = false
let requestSequence = 0
let saving = false
let dirty = false // edits on screen not yet saved
let pendingSave = false // a save waiting for signal; when it lands, the page moves on as if it had saved at once
let notice: string | null = null // shown once on the open question, e.g. a waiting save that was refused

const example: Job & { windows: number; size: string; postcode: string; roof_windows: number; sqm: number; kind: JobKind } = {
  windows: 14,
  size: '3 bed',
  postcode: '',
  roof_windows: 0,
  how_often: 'w4',
  fronts_only: false,
  sqm: 40,
  kind: 'window',
}

const copy = (l: PriceList): PriceList => JSON.parse(JSON.stringify(l)) as PriceList

// Whole pounds read cleaner on this page ("£35", "£2.50"): quotes come out rounded, so ".00" is just noise.
function money(pence: number): string {
  const full = moneyWithPence(pence)
  return full.endsWith('.00') ? full.slice(0, -3) : full
}

function stepsFor(list: PriceList | null): StepName[] {
  const all: StepName[] = ['how', 'price', 'areas', 'first', 'often', 'extras', 'least', 'check']
  // A job priced by its size has no windows, insides, regulars' discount or first-clean extra to ask about.
  if (list?.method === 'by_area') return ['how', 'price', 'least', 'check']
  return list?.method === 'house_size' ? all.filter((s) => s !== 'areas') : all
}

function root(): HTMLElement {
  return document.querySelector<HTMLElement>('#screen')!
}

function touched(): void {
  dirty = true
}

// ---- small building blocks ----------------------------------------------------------------------------------

function chips<T>(options: [string, T][], current: T | undefined, onPick: (v: T) => void): HTMLElement {
  const box = el('div', { class: 'ps-chips', role: 'group' })
  for (const [label, value] of options) {
    const b = el('button', { type: 'button', class: 'ps-chip' + (current === value ? ' is-picked' : '') }, label)
    b.setAttribute('aria-pressed', current === value ? 'true' : 'false')
    b.addEventListener('click', () => onPick(value))
    box.append(b)
  }
  return box
}

function moneyInput(value: number | null, onChange: (pence: number | null) => void, opts: { placeholder?: string; big?: boolean; label: string }): HTMLInputElement {
  const input = el('input', { type: 'text', inputmode: 'decimal', class: opts.big ? 'ps-big-input' : 'ps-small-input', 'aria-label': opts.label, placeholder: opts.placeholder ?? '' })
  input.value = value == null ? '' : (value / 100).toFixed(2)
  input.addEventListener('input', () => {
    try {
      onChange(parseMoney(input.value))
      input.setCustomValidity('')
    } catch (e) {
      input.setCustomValidity((e as Error).message)
    }
  })
  return input
}

function percentInput(value: number, onChange: (n: number) => void, label: string, max = 1000): HTMLInputElement {
  const input = el('input', { type: 'number', inputmode: 'numeric', min: '0', max: String(max), step: '1', class: 'ps-pct-input', 'aria-label': label })
  input.value = String(value)
  input.addEventListener('input', () => onChange(Math.max(0, Math.min(max, Math.round(Number(input.value) || 0)))))
  return input
}

function rowline(label: string, ...right: HTMLElement[]): HTMLElement {
  return el('div', { class: 'ps-rowline' }, el('span', { class: 'ps-rowline-label' }, label), el('div', { class: 'ps-rowline-right' }, ...right))
}

// ---- the questions --------------------------------------------------------------------------------------------
// Each returns the card body. `redraw` repaints the whole step (after a tap that changes what is shown);
// `refresh` only repaints the side panel and the Next button (after typing, so the cursor stays put).

interface StepParts {
  title: string
  help?: string
  body: HTMLElement
  ready: boolean
  noNext?: boolean
}

function question(name: StepName, list: PriceList, redraw: () => void, refresh: () => void): StepParts {
  const body = el('div', { class: 'ps-body' })
  const answer = (): void => {
    answered.add(name)
    touched()
  }

  if (name === 'how') {
    const row = el('div', { class: 'ps-choices' })
    const options: [PriceMethod, string, string][] = [
      ['per_window', 'Per window', `A price for each window, like ${currencySymbol()}2.50 a window.`],
      ['house_size', 'By house size', 'A price for a flat, a 2 bed, a 3 bed and so on.'],
      ['by_area', 'By size (square metres)', `A price a square metre for each kind of job, like ${currencySymbol()}3.50 a m² for a patio.`],
    ]
    const known = answered.has('how') || mode === 'change'
    for (const [method, title, sub] of options) {
      const b = el('button', { type: 'button', class: 'ps-choice' + (known && list.method === method ? ' is-picked' : '') }, el('strong', {}, title), el('span', {}, sub))
      b.addEventListener('click', () => {
        if (!known || list.method !== method) {
          // Keep everything that means the same either way; only the base price starts again.
          const fresh = blankPriceList(method)
          Object.assign(list, { method, window_pence: fresh.window_pence, area_prices: fresh.area_prices, sizes: fresh.sizes, size_rates: fresh.size_rates })
          answered.delete('price')
          answered.delete('areas')
          areasOn = method === 'per_window' ? null : false
        }
        answer()
        // From the summary, a new way of pricing has no prices yet: go straight on to them, so Save can only
        // ever send a complete list (Astra, 1 Oct).
        if (mode === 'change') {
          if (!list.window_pence && !list.sizes.some((x) => x.pence > 0) && !pricedKinds(list).length) changing = 'price'
          redraw()
        } else next()
      })
      row.append(b)
    }
    body.append(row)
    return { title: 'How do you price?', help: 'Roundly uses this to write quotes for you. You can change it any time.', body, ready: known, noNext: mode !== 'change' }
  }

  if (name === 'price') {
    if (list.method === 'by_area') {
      const rates = sizeRates(list)
      for (const k of KINDS) {
        const input = moneyInput(rates[k], (p) => { list.size_rates = { ...sizeRates(list), [k]: p }; if (p) answered.add('price'); touched(); refresh() }, { label: `${kindLabel(k)}, price a square metre`, placeholder: '0.00' })
        body.append(rowline(kindLabel(k), el('span', { class: 'ps-inline' }, el('span', { class: 'ps-pound' }, currencySymbol()), input, el('span', {}, 'a m²'))))
      }
      return { title: 'What do you charge a square metre?', help: 'For each kind of job. Leave a job empty if you don’t price it this way.', body, ready: pricedKinds(list).length > 0 }
    }
    if (list.method === 'per_window') {
      const input = moneyInput(list.window_pence, (p) => { list.window_pence = p; if (p) answered.add('price'); touched(); refresh() }, { big: true, label: 'Price per window', placeholder: '2.50' })
      input.required = true
      body.append(el('label', { class: 'ps-big' }, el('span', {}, currencySymbol()), input, el('small', {}, 'a window')))
      body.append(chips<number>([[`${currencySymbol()}2`, 200], [`${currencySymbol()}2.50`, 250], [`${currencySymbol()}3`, 300]], list.window_pence ?? undefined, (v) => { list.window_pence = v; answer(); redraw() }))
      return { title: 'What do you charge per window?', help: 'For a regular clean of the outside.', body, ready: (list.window_pence ?? 0) > 0 }
    }
    list.sizes.forEach((s, i) => {
      const label = el('input', { type: 'text', maxlength: '40', class: 'ps-text-input', 'aria-label': 'House size' })
      label.value = s.label
      label.required = true
      label.addEventListener('input', () => { s.label = label.value; touched(); refresh() })
      const price = moneyInput(s.pence || null, (p) => { s.pence = p ?? 0; if (p) answered.add('price'); touched(); refresh() }, { label: `Price for ${s.label || 'this size'}`, placeholder: '0.00' })
      price.required = true
      const remove = el('button', { type: 'button', class: 'link-button' }, 'Remove')
      remove.addEventListener('click', () => { list.sizes.splice(i, 1); touched(); redraw() })
      body.append(el('div', { class: 'ps-size' }, label, el('span', { class: 'ps-inline' }, el('span', { class: 'ps-pound' }, currencySymbol()), price), remove))
    })
    const add = el('button', { type: 'button', class: 'link-button ps-add' }, 'Add a size')
    add.addEventListener('click', () => { list.sizes.push({ label: '', pence: 0 }); touched(); redraw() })
    body.append(add)
    return { title: 'What do you charge for each size?', help: 'For a regular clean of the outside. Remove any size you don’t do.', body, ready: list.sizes.some((s) => s.pence > 0) }
  }

  if (name === 'areas') {
    const on = areasOn ?? (answered.has('areas') || mode === 'change' ? list.area_prices.length > 0 : null)
    body.append(
      chips<boolean>([['Same price everywhere', false], ['Some areas cost more', true]], on ?? undefined, (v) => {
        areasOn = v
        if (!v) list.area_prices = []
        else if (list.area_prices.length === 0) list.area_prices.push({ starts: [], window_pence: list.window_pence ?? 0 })
        answer()
        redraw()
      }),
    )
    if (on) {
      list.area_prices.forEach((a, i) => {
        const starts = el('input', { type: 'text', placeholder: AREA_HINT[roundConfig().country].example, class: 'ps-text-input', 'aria-label': AREA_HINT[roundConfig().country].label })
        starts.value = a.starts.join(', ')
        starts.required = true
        starts.addEventListener('input', () => {
          a.starts = starts.value.split(/[,\s]+/).map((x) => x.trim().toUpperCase()).filter(Boolean)
          touched()
          refresh()
        })
        const price = moneyInput(a.window_pence || null, (p) => { a.window_pence = p ?? 0; touched(); refresh() }, { label: postcodeWords('Price per window in these postcodes'), placeholder: '3.00' })
        price.required = true
        const remove = el('button', { type: 'button', class: 'link-button' }, 'Remove')
        remove.addEventListener('click', () => {
          list.area_prices.splice(i, 1)
          if (list.area_prices.length === 0) areasOn = false
          touched()
          redraw()
        })
        body.append(
          el('div', { class: 'ps-area' },
            el('label', {}, el('span', {}, AREA_HINT[roundConfig().country].label), starts),
            el('label', {}, el('span', {}, 'Per window'), el('span', { class: 'ps-inline' }, el('span', { class: 'ps-pound' }, currencySymbol()), price)),
            remove,
          ),
        )
      })
      const add = el('button', { type: 'button', class: 'link-button ps-add' }, 'Add another area')
      add.addEventListener('click', () => { list.area_prices.push({ starts: [], window_pence: list.window_pence ?? 0 }); touched(); redraw() })
      body.append(add, el('p', { class: 'ps-note' }, 'Customers are never told their price depends on where they live.'))
    }
    return { title: 'Do some areas cost more?', body, ready: on !== null }
  }

  if (name === 'first') {
    const preset = [0, 10, 20, 25, 50]
    const picked = answered.has('first') || mode === 'change' ? list.first_clean_extra_pct : undefined
    body.append(
      chips<number>([['Same price', 0], ['10% more', 10], ['20% more', 20], ['25% more', 25], ['50% more', 50]], picked, (v) => { list.first_clean_extra_pct = v; answer(); redraw() }),
    )
    // On top of the smallest job too (Liam, 16 Oct 2026: £25 for a regular clean, £35 for the first). Off: the extra is
    // added before the smallest job price, as lists always worked, so a small job's first clean may be that price itself.
    // Shown once the first clean costs more; the example follows the % as it is typed.
    const over = el('input', { type: 'checkbox' })
    over.checked = list.first_clean_over_minimum === true
    over.addEventListener('change', () => { list.first_clean_over_minimum = over.checked; touched(); refresh() })
    const overWords = el('span', {})
    const overTick = el('label', { class: 'check-label ps-check' }, over, overWords)
    const paintOver = (): void => {
      const least = list.minimum_pence ?? 2500
      overWords.textContent = ` Smallest jobs too: a ${money(least)} visit's first clean is ${money(Math.round(least * (100 + list.first_clean_extra_pct) / 100))}`
      overTick.hidden = list.method === 'by_area' || list.first_clean_extra_pct <= 0
    }
    paintOver()
    const other = percentInput(list.first_clean_extra_pct, (n) => { list.first_clean_extra_pct = n; answered.add('first'); touched(); paintOver(); refresh() }, 'First clean, % more')
    if (picked !== undefined && !preset.includes(picked)) other.classList.add('is-picked')
    body.append(el('label', { class: 'ps-other' }, el('span', {}, 'Or type it:'), other, el('span', {}, '% more')), overTick)
    return { title: 'Does the first clean cost more?', help: 'Most cleaners charge more the first time, to get everything up to scratch.', body, ready: picked !== undefined }
  }

  if (name === 'often') {
    for (const h of HOW_OFTEN) {
      const off = 100 - oftenPct(list, h.value)
      const custom = percentInput(off, (n) => { list.how_often_pct[h.value] = 100 - Math.min(n, 100); touched(); refresh() }, `${h.label}, % off`, 100)
      body.append(
        rowline(
          h.label,
          chips<number>([['None', 0], ['5% off', 5], ['10% off', 10]], off, (v) => { list.how_often_pct[h.value] = 100 - v; touched(); redraw() }),
          el('span', { class: 'ps-inline' }, custom, el('span', {}, '% off')),
        ),
      )
    }
    const firstToo = el('input', { type: 'checkbox' })
    firstToo.checked = list.first_clean_gets_discount
    firstToo.addEventListener('change', () => { list.first_clean_gets_discount = firstToo.checked; touched(); refresh() })
    body.append(el('label', { class: 'check-label ps-check' }, firstToo, ' Take the discount off the first clean too'))
    return { title: 'Do regulars get a discount?', help: 'Pick None if every visit is the same price.', body, ready: true }
  }

  if (name === 'extras') {
    const insidePicked = answered.has('extras') || mode === 'change' ? list.inside_pct : undefined
    body.append(el('h4', { class: 'ps-sub' }, 'Insides as well'))
    // "Same as the outside" and a typed amount too (Liam, 9 Oct 2026: "This should have a same as outside price or a
    // custom amount box"), typed as a share of the outside price, as the first clean's extra is.
    body.append(chips<number>([['We don’t do insides', 0], ['Same as the outside', 100], ['1.5 x the outside', 150], ['2 x the outside', 200]], insidePicked, (v) => { list.inside_pct = v; answer(); redraw() }))
    const insideOther = percentInput(list.inside_pct, (n) => { list.inside_pct = n; answered.add('extras'); touched(); refresh() }, 'Insides, % of the outside price')
    if (insidePicked !== undefined && ![0, 100, 150, 200].includes(insidePicked)) insideOther.classList.add('is-picked')
    body.append(el('label', { class: 'ps-other' }, el('span', {}, 'Or type it:'), insideOther, el('span', {}, '% of the outside')))
    body.append(el('h4', { class: 'ps-sub' }, 'Roof windows (Velux)'))
    body.append(
      chips<number | null>([['Same as a window', null], [`${currencySymbol()}5 each`, 500], [`${currencySymbol()}7.50 each`, 750], [`${currencySymbol()}10 each`, 1000]], list.roof_window_pence, (v) => { list.roof_window_pence = v; touched(); redraw() }),
    )
    const roof = moneyInput(list.roof_window_pence, (p) => { list.roof_window_pence = p; touched(); refresh() }, { label: 'Price for each roof window', placeholder: 'as a window' })
    body.append(el('label', { class: 'ps-other' }, el('span', {}, `Or type it: ${currencySymbol()}`), roof, el('span', {}, 'each')))
    return { title: 'Any extras?', body, ready: insidePicked !== undefined }
  }

  if (name === 'least') {
    const min = moneyInput(list.minimum_pence, (p) => { list.minimum_pence = p; touched(); refresh() }, { big: true, label: 'The least a visit costs', placeholder: 'none' })
    body.append(el('label', { class: 'ps-big' }, el('span', {}, currencySymbol()), min, el('small', {}, 'the least a visit costs')))
    if (list.method !== 'by_area') {
      const fronts = moneyInput(list.fronts_minimum_pence, (p) => { list.fronts_minimum_pence = p; touched(); refresh() }, { label: 'The least a fronts-only visit costs', placeholder: 'same' })
      body.append(rowline('Fronts only, the least is', el('span', { class: 'ps-inline' }, el('span', { class: 'ps-pound' }, currencySymbol()), fronts)))
    }
    body.append(el('h4', { class: 'ps-sub' }, 'Round prices'))
    const roundPicked = answered.has('least') || mode === 'change' ? list.round_to_pence : undefined
    body.append(chips<number>([['Don’t round', 0], [`To ${currencySymbol()}1`, 100], [`To ${currencySymbol()}5`, 500], [`To ${currencySymbol()}10`, 1000]], roundPicked, (v) => { list.round_to_pence = v; answer(); redraw() }))
    if (list.round_to_pence) {
      body.append(chips<PriceList['round_mode']>([['Always up', 'up'], ['To the nearest', 'nearest']], list.round_mode, (v) => { list.round_mode = v; touched(); redraw() }))
      body.append(el('p', { class: 'ps-note' }, 'A price is never rounded below the least a visit costs.'))
    }
    return { title: 'Your smallest job, and rounding', help: `So a quote never comes out at ${currencySymbol()}27.40.`, body, ready: roundPicked !== undefined }
  }

  // check: a few jobs priced with the answers so far
  const firstArea = list.area_prices[0]?.starts[0]
  const jobs: [string, Job][] =
    list.method === 'by_area'
      ? pricedKinds(list).flatMap((k) => [[`${kindLabel(k)}, 20 m²`, { sqm: 20, kind: k, how_often: 'one_off' }], [`${kindLabel(k)}, 60 m²`, { sqm: 60, kind: k, how_often: 'one_off' }]] as [string, Job][])
      : list.method === 'per_window'
      ? [
          ['14 windows, every 4 weeks', { windows: 14, how_often: 'w4', postcode: firstArea ? 'ZZ1' : '' }],
          ...(firstArea ? ([[`The same house in ${firstArea}`, { windows: 14, how_often: 'w4', postcode: firstArea }]] as [string, Job][]) : []),
          ['8 windows, fronts only, one-off', { windows: 8, how_often: 'one_off', fronts_only: true, postcode: firstArea ? 'ZZ1' : '' }],
        ]
      : list.sizes.filter((s) => s.pence > 0).slice(0, 3).map((s) => [`${s.label}, every 4 weeks`, { size: s.label, how_often: 'w4' }] as [string, Job])
  for (const [label, job] of jobs) {
    const q = quote(list, job, currencySymbol(), roundConfig().country)
    if (list.method === 'by_area') body.append(rowline(label, el('strong', {}, money(q.regular_pence))))
    else body.append(rowline(label, el('strong', {}, `${money(q.regular_pence)} a visit`), el('span', { class: 'ps-muted' }, `first clean ${money(q.first_pence)}`)))
  }
  body.append(el('p', { class: 'ps-note' }, 'Try any job in the box beside this. If a price looks wrong, go back and change that step.'))
  return { title: 'Does this look right?', help: 'A few jobs, priced with what you just told Roundly.', body, ready: true }
}

// ---- drawing --------------------------------------------------------------------------------------------------

function heading(lede: string): HTMLElement {
  return el('div', { class: 'ps-head' }, el('h2', {}, 'Your prices'), el('p', { class: 'muted' }, lede))
}

function draw(): void {
  const r = root()
  clear(r)
  if (!payload) return
  if (mode === 'setup' && !draft) {
    // How they said they price at sign-up (0073; Liam picked B, 15 Oct 2026) is picked already; one tap goes on.
    const said = getMe()?.priceMethod ?? null
    draft = blankPriceList(said ?? 'per_window')
    answered = new Set(said ? ['how'] : [])
    areasOn = null
  }
  const layout = el('div', { class: 'ps-layout' })
  const main = el('div', { class: 'ps-main' })
  const side = el('aside', { class: 'ps-side' })
  layout.append(main, side)

  if (mode === 'summary') drawSummary(main)
  else if (mode === 'done') drawDone(main)
  else drawStep(main, side)

  if (mode === 'setup' && stepIndex < stepsFor(draft).length - 1) paintSoFar(side, draft!)
  else paintTry(side, draft!)
  r.append(layout)
}

function next(): void {
  stepIndex = Math.min(stepIndex + 1, stepsFor(draft).length - 1)
  draw()
  window.scrollTo(0, 0)
}

function drawStep(main: HTMLElement, side: HTMLElement): void {
  const list = draft!
  const steps = stepsFor(list)
  const name: StepName = mode === 'change' ? changing! : steps[stepIndex]!
  if (mode === 'setup') {
    main.append(heading('A few quick questions so Roundly can price quotes for you. About two minutes.'))
    const progress = el('div', { class: 'ps-progress' }, el('span', {}, `Step ${stepIndex + 1} of ${steps.length}`))
    const dots = el('div', { class: 'ps-dots', 'aria-hidden': 'true' })
    steps.forEach((_, i) => dots.append(el('span', { class: 'ps-dot' + (i < stepIndex ? ' is-done' : i === stepIndex ? ' is-on' : '') })))
    progress.append(dots)
    main.append(progress)
  } else {
    main.append(heading('Change this, then Save. New quotes use it; quotes already sent don’t change.'))
  }

  const form = el('form', { class: 'ps-card ps-step-in', novalidate: 'novalidate' })
  const isLast = mode === 'setup' && stepIndex === steps.length - 1
  const go = el('button', { type: 'submit', class: 'btn-primary ps-go' }, mode === 'change' ? 'Save' : isLast ? 'Save my prices' : 'Next')
  const setReady = (ready: boolean): void => {
    go.disabled = !ready
    // Glows once it can be pressed, like the first-booking guide (29 Sep). Only while setting up.
    go.classList.toggle('guide-glow', ready && mode === 'setup')
  }
  const redraw = (): void => draw()
  const refresh = (): void => {
    if (mode === 'setup' && !isLast) paintSoFar(side, list)
    else paintTry(side, list)
    setReady(question(name, list, () => {}, () => {}).ready)
  }
  const parts = question(name, list, redraw, refresh)
  form.append(el('h3', { class: 'ps-title' }, parts.title))
  if (parts.help) form.append(el('p', { class: 'ps-help' }, parts.help))
  form.append(parts.body)
  setReady(parts.ready)

  const error = el('p', { class: 'form-error', role: 'alert' }, notice ?? '')
  notice = null
  const foot = el('div', { class: 'ps-foot' })
  const back = el('button', { type: 'button', class: 'link-button' }, mode === 'change' ? 'Cancel' : 'Back')
  back.addEventListener('click', () => {
    if (mode === 'change') {
      draft = saved ? copy(saved) : draft
      dirty = false
      mode = 'summary'
      changing = null
    } else stepIndex = Math.max(0, stepIndex - 1)
    draw()
  })
  foot.append(mode === 'change' || stepIndex > 0 ? back : el('span', {}))
  if (!parts.noNext) foot.append(go)
  form.append(error, foot)

  form.addEventListener('submit', (e) => {
    e.preventDefault()
    if (go.disabled || !form.reportValidity()) return
    if (mode === 'change' || isLast) void save(form, error, go)
    else next()
  })
  main.append(form)
}

function summaryLines(list: PriceList): [StepName, string, string][] {
  const often = HOW_OFTEN.map((h) => {
    const off = 100 - oftenPct(list, h.value)
    return `${h.label.replace('Every ', '')} ${off ? `${off}% off` : 'full price'}`
  })
  if (list.method === 'by_area') {
    const kinds = pricedKinds(list)
    return [
      ['how', 'How you price', 'By size (square metres)'],
      ['price', 'Price a square metre', kinds.map((k) => `${kindLabel(k)} ${money(sizeRates(list)[k]!)}`).join(' · ') || 'not set'],
      ['least', 'Smallest job', `${list.minimum_pence != null ? money(list.minimum_pence) : 'No minimum'} · ${list.round_to_pence ? `rounded ${list.round_mode === 'up' ? 'up ' : ''}to ${money(list.round_to_pence)}` : 'not rounded'}`],
    ]
  }
  const lines: [StepName, string, string][] = [
    ['how', 'How you price', list.method === 'per_window' ? 'Per window' : 'By house size'],
    list.method === 'per_window'
      ? ['price', 'Price per window', list.window_pence ? money(list.window_pence) : 'not set']
      : ['price', 'House sizes', list.sizes.filter((s) => s.pence > 0).map((s) => `${s.label} ${money(s.pence)}`).join(' · ') || 'not set'],
  ]
  if (list.method === 'per_window') {
    lines.push(['areas', 'Areas that cost more', list.area_prices.length ? list.area_prices.map((a) => `${a.starts.join(', ') || '?'} at ${money(a.window_pence)}`).join(' · ') : 'Same price everywhere'])
  }
  lines.push(
    ['first', 'First clean', list.first_clean_extra_pct ? `${list.first_clean_extra_pct}% more${list.first_clean_over_minimum ? ', the smallest job too' : ''}` : 'Same price'],
    ['often', 'Regulars', often.join(' · ') + (list.first_clean_gets_discount ? ' · first clean too' : '')],
    ['extras', 'Extras', `${list.inside_pct ? `Insides ${list.inside_pct}% of the outside` : 'No insides'} · ${list.roof_window_pence != null ? `Velux ${money(list.roof_window_pence)} each` : 'Velux as a window'}`],
    [
      'least',
      'Smallest job',
      `${list.minimum_pence != null ? money(list.minimum_pence) : 'No minimum'}${list.fronts_minimum_pence != null ? `, fronts only ${money(list.fronts_minimum_pence)}` : ''} · ${list.round_to_pence ? `rounded ${list.round_mode === 'up' ? 'up ' : ''}to ${money(list.round_to_pence)}` : 'not rounded'}`,
    ],
  )
  return lines
}

function drawSummary(main: HTMLElement): void {
  main.append(heading('What Roundly uses to write your quotes. Change anything that has moved on.'))
  const card = el('section', { class: 'ps-card ps-summary ps-step-in' })
  for (const [step, what, value] of summaryLines(draft!)) {
    const change = el('button', { type: 'button', class: 'link-button' }, 'Change')
    change.addEventListener('click', () => {
      mode = 'change'
      changing = step
      areasOn = draft!.area_prices.length > 0
      justSaved = false
      draw()
    })
    card.append(el('div', { class: 'ps-sum-row' }, el('div', {}, el('div', { class: 'ps-sum-what' }, what), el('div', { class: 'ps-sum-value' }, value)), change))
  }
  main.append(card)
  if (justSaved) main.append(el('p', { class: 'ps-saved' }, '✓ Saved. New quotes use this. Quotes already sent don’t change.'))
}

function drawDone(main: HTMLElement): void {
  main.append(heading('All set.'))
  const card = el('section', { class: 'ps-card ps-done ps-step-in' })
  card.append(
    el('div', { class: 'ps-tick', 'aria-hidden': 'true' }, '✓'),
    el('h3', { class: 'ps-title' }, 'Your prices are saved'),
    el('p', { class: 'ps-help' }, 'From now on, Roundly writes each quote with these prices. You check it and tap Send.'),
  )
  const see = el('button', { type: 'button', class: 'btn-primary' }, 'See your prices')
  see.addEventListener('click', () => { mode = 'summary'; draw() })
  card.append(see)
  main.append(card)
}

// ---- the side panel -------------------------------------------------------------------------------------------

function paintSoFar(side: HTMLElement, list: PriceList): void {
  clear(side)
  side.append(el('h3', {}, 'Your prices so far'))
  const ul = el('ul', { class: 'ps-sofar' })
  const steps = stepsFor(list)
  for (const [step, what, value] of summaryLines(list)) {
    // Shown once answered, or once passed (the regulars question has a sensible answer already: none).
    const known = answered.has(step) || steps.indexOf(step) < stepIndex
    ul.append(el('li', { class: known ? '' : 'is-todo' }, el('span', {}, what), el('span', {}, known ? value : 'not yet')))
  }
  side.append(ul)
  if (!answered.has('how')) return
  const priced = list.sizes.filter((s) => s.pence > 0)
  const size = (priced.find((s) => s.label === '3 bed') ?? priced[Math.min(2, priced.length - 1)])?.label ?? ''
  const kind = pricedKinds(list)[0] ?? 'window'
  const job: Job = list.method === 'by_area' ? { sqm: 40, kind, how_often: 'one_off' } : list.method === 'per_window' ? { windows: 14, how_often: 'w4' } : { size, how_often: 'w4' }
  const q = quote(list, job, currencySymbol(), roundConfig().country)
  const box = el('div', { class: 'ps-example' })
  box.append(el('span', { class: 'ps-example-job' }, list.method === 'by_area' ? `${kindLabel(kind)}, 40 m²` : list.method === 'per_window' ? 'A house with 14 windows, every 4 weeks' : `A ${job.size || 'house'}, every 4 weeks`))
  if (q.status === 'priced') {
    const range = q.regular_range_pence
    const first = q.first_range_pence
    box.append(
      el('span', { class: 'ps-example-price' }, range ? `${money(range[0])} to ${money(range[1])}` : list.method === 'by_area' ? money(q.regular_pence) : `${money(q.regular_pence)} a visit`),
      ...(list.method === 'by_area' ? [] : [el('span', { class: 'ps-example-first' }, `First clean ${first ? `${money(first[0])} to ${money(first[1])}` : money(q.first_pence)}`)]),
    )
  } else box.append(el('span', { class: 'ps-example-first' }, 'Add a price to see it.'))
  side.append(box)
}

// "Try a job": the owner describes one job and Roundly prices it with the list as it stands on screen.
function paintTry(side: HTMLElement, list: PriceList): void {
  clear(side)
  side.append(el('h3', {}, 'Try a job'))
  const inputs = el('div', { class: 'ps-try-inputs' })
  const result = el('div', { class: 'ps-try-result', 'aria-live': 'polite' })
  const rerun = (): void => paintResult(result, list)
  const field = (label: string, node: HTMLElement): HTMLElement => el('label', { class: 'booking-field' }, el('span', {}, label), node)

  if (list.method === 'by_area') {
    const m = el('input', { type: 'number', min: '1', max: '10000', step: '0.1', inputmode: 'decimal' })
    m.value = String(example.sqm)
    m.addEventListener('input', () => { example.sqm = Math.max(0, Number(m.value) || 0); rerun() })
    inputs.append(field('Size (m²)', m))
    const k = el('select', {}) as HTMLSelectElement
    const kinds = pricedKinds(list)
    if (!kinds.includes(example.kind) && kinds.length) example.kind = kinds[0]!
    for (const kind of kinds) {
      const o = el('option', { value: kind }, kindLabel(kind))
      if (kind === example.kind) o.selected = true
      k.append(o)
    }
    k.addEventListener('change', () => { example.kind = k.value as JobKind; rerun() })
    inputs.append(field('Which job', k))
    side.append(inputs, result)
    rerun()
    return
  }
  if (list.method === 'per_window') {
    const w = el('input', { type: 'number', min: '1', max: '200', inputmode: 'numeric' })
    w.value = String(example.windows)
    w.addEventListener('input', () => { example.windows = Math.max(0, Math.round(Number(w.value) || 0)); rerun() })
    inputs.append(field('Windows', w))
  } else {
    const s = el('select', {}) as HTMLSelectElement
    const labels = list.sizes.filter((x) => x.pence > 0).map((x) => x.label.trim()).filter(Boolean)
    if (!labels.includes(example.size) && labels.length) example.size = labels[Math.min(2, labels.length - 1)]!
    for (const label of labels) {
      const o = el('option', { value: label }, label)
      if (label === example.size) o.selected = true
      s.append(o)
    }
    s.addEventListener('change', () => { example.size = s.value; rerun() })
    inputs.append(field('House size', s))
  }
  if (list.method === 'per_window' && list.area_prices.length) {
    const pc = el('input', { type: 'text', placeholder: 'e.g. N10 3AB', maxlength: '10' })
    pc.value = example.postcode
    pc.addEventListener('input', () => { example.postcode = pc.value; rerun() })
    inputs.append(field(postcodeWords('Postcode'), pc))
  }
  const r = el('input', { type: 'number', min: '0', max: '50', inputmode: 'numeric' })
  r.value = String(example.roof_windows)
  r.addEventListener('input', () => { example.roof_windows = Math.max(0, Math.round(Number(r.value) || 0)); rerun() })
  inputs.append(field('Roof windows', r))
  const ho = el('select', {}) as HTMLSelectElement
  for (const h of HOW_OFTEN) {
    const o = el('option', { value: h.value }, h.label)
    if (h.value === example.how_often) o.selected = true
    ho.append(o)
  }
  ho.addEventListener('change', () => { example.how_often = ho.value as HowOften; rerun() })
  inputs.append(field('How often', ho))
  const fronts = el('input', { type: 'checkbox' })
  fronts.checked = !!example.fronts_only
  fronts.addEventListener('change', () => { example.fronts_only = fronts.checked; rerun() })
  inputs.append(el('label', { class: 'check-label' }, fronts, ' Fronts only'))
  side.append(inputs, result)
  rerun()
}

function paintResult(box: HTMLElement, list: PriceList): void {
  clear(box)
  const q = quote(list, example, currencySymbol(), roundConfig().country)
  if (q.status === 'needs_details') {
    box.append(el('p', { class: 'ps-note' }, list.method === 'by_area' ? 'Add a size in square metres to see a price.' : list.method === 'per_window' ? 'Add a number of windows to see a price.' : 'Add a price for a size to see a price.'))
    return
  }
  const show = (one: number, range: [number, number] | null): string => (range ? `${money(range[0])} to ${money(range[1])}` : money(one))
  const rows: [string, string][] = list.method === 'by_area' ? [['The job', show(q.regular_pence, q.regular_range_pence)]] : [['Every visit', show(q.regular_pence, q.regular_range_pence)], ['First clean', show(q.first_pence, q.first_range_pence)]]
  if (q.inside_pence != null) rows.push(['Insides as well, add', show(q.inside_pence, q.inside_range_pence)])
  const dl = el('dl', { class: 'ps-figures' })
  for (const [k, v] of rows) dl.append(el('dt', {}, k), el('dd', {}, v))
  box.append(dl)
  if (q.regular_range_pence) box.append(el('p', { class: 'ps-note' }, postcodeWords('A range, because the postcode is missing and some areas have their own price.')))
  const how = el('details', {}, el('summary', {}, 'How it’s worked out'))
  const ul = el('ul', {})
  for (const line of q.lines) ul.append(el('li', {}, postcodeWords(line)))
  how.append(ul)
  box.append(how)
}

// ---- saving ---------------------------------------------------------------------------------------------------

async function save(form: HTMLFormElement, error: HTMLElement, go: HTMLButtonElement): Promise<void> {
  if (saving || !payload || !draft) return
  error.textContent = ''
  saving = true
  const controls = Array.from(form.querySelectorAll<HTMLInputElement | HTMLButtonElement | HTMLSelectElement>('input, button, select'))
  controls.forEach((n) => (n.disabled = true))
  const gen = routeGen // the owner may leave the page before the answer comes
  // A copy: later typing must never change a save that is waiting for signal (Astra, 1 Oct).
  const sent = copy(draft)
  const body = { version: payload.version, list: { ...sent, schema: PRICE_LIST_SCHEMA, sizes: sent.sizes.map((s) => ({ ...s, label: s.label.trim() })) } }
  const ack = await mutate({ label: 'Save your prices', method: 'PATCH', path: '/api/prices', body })
  if (gen !== routeGen) return // left the page: this answer belongs to a visit that is over, so touch nothing
  saving = false
  if (ack.ok) {
    const wasFirst = mode === 'setup'
    dirty = false
    justSaved = !wasFirst
    mode = wasFirst ? 'done' : 'summary'
    changing = null
    await load(true)
    return
  }
  controls.forEach((n) => (n.disabled = false))
  go.classList.remove('guide-glow')
  if (ack.status === 409) {
    // The clashing save stays in the waiting list as a conflict, and a new Save would wait behind it for ever:
    // clear it, as the bank screen does, since what is on screen replaces it (Astra, 1 Oct).
    for (const q of getQueue()) if (q.status === 'conflict' && q.path === '/api/prices') dismiss(q.key)
    // Saved somewhere else meanwhile. Keep what is on screen; take the newest version so the next Save goes through.
    try {
      const latest = await api<PricesPayload>('/api/prices')
      if (gen !== routeGen) return // left the page meanwhile (Astra, 1 Oct)
      payload.version = latest.version
      error.textContent = 'Your prices were saved from somewhere else too. What you see here is yours: check it and save again.'
    } catch {
      if (gen !== routeGen) return
      error.textContent = 'Your prices were saved from somewhere else too. Reload the page to see them.'
    }
  } else if (ack.status >= 400 && ack.status < 500) {
    error.textContent = ack.error ?? 'Check the prices and try again.'
  } else if (ack.signedOut) {
    error.textContent = 'Your sign-in has expired. Sign in again and try saving.'
  } else {
    error.textContent = 'Waiting for signal. Your prices will save once the connection is back.'
    dirty = false // what is on screen is what will be saved; once it is, the page refreshes itself
    pendingSave = true
  }
}

// ---- loading --------------------------------------------------------------------------------------------------

// A queued save behind a refused one carries the same stale version and would clash too: what is on screen
// replaces both, so it is dropped and the owner saves once more.
function cancelQueuedSave(key: string): void {
  cancelQueued(key)
}

// The newest version and saved list, without touching what is on screen: the next Save then goes through, and
// Cancel goes back to what is really saved (Astra, 1 Oct).
async function refreshVersion(): Promise<void> {
  const gen = routeGen
  try {
    const latest = await api<PricesPayload>('/api/prices')
    if (gen !== routeGen || !payload) return
    payload.version = latest.version
    saved = latest.list ? copy(latest.list) : null
  } catch {
    // No signal again: the next Save answers with a clash and the page recovers then.
  }
}

// keepMode: after a save, show the tick or the summary rather than starting over.
async function load(keepMode = false): Promise<void> {
  const gen = routeGen
  const sequence = ++requestSequence
  try {
    const result = await api<PricesPayload>('/api/prices')
    if (gen !== routeGen || sequence !== requestSequence) return
    if (dirty || saving) return // the owner started typing while this was on its way (Astra, 1 Oct)
    payload = result
    saved = result.list ? copy(result.list) : null
    draft = result.list ? copy(result.list) : null
    if (!keepMode || !result.list) {
      mode = result.list ? 'summary' : 'setup'
      stepIndex = 0
      changing = null
      justSaved = false
      answered = new Set()
      areasOn = null
    }
    draw()
    dirty = false
  } catch (err) {
    if (gen !== routeGen || sequence !== requestSequence) return
    if (dirty || saving) return // a failed background refresh never wipes typing (Astra, 1 Oct)
    clear(root())
    loadError(root(), (err as Error).message, () => void load())
  }
}

export function mountPrices(): void {
  // A fresh visit: a save still on its way from an earlier visit finishes on its own and touches nothing here.
  saving = false
  pendingSave = false
  notice = null
  draft = null
  saved = null
  dirty = false
  void load()
}

export function pricesOnDataChanged(): void {
  // Unsaved answers are kept: a background refresh must not wipe them. The summary and the tick refresh.
  if (saving) return
  if (!pendingSave && dirty) return
  if (pendingSave) {
    // A save that waited for signal. Refused or clashed once it was sent: keep what is on screen and say so, so
    // the owner can save again (Astra, 1 Oct). Checked before "still waiting", because a second save queued behind
    // a refused one would otherwise wait for ever; the refused entries are cleared so nothing waits behind them.
    const mine = getQueue().filter((q) => q.path === '/api/prices')
    const refused = mine.filter((q) => q.status === 'conflict' || q.status === 'failed')
    // Anything else still in the list (waiting, sending, or held for a sign-in) has not landed yet (Astra, 1 Oct).
    if (!refused.length && mine.length > 0) return
    pendingSave = false
    if (refused.length) {
      for (const q of mine) if (q.status === 'conflict' || q.status === 'failed') dismiss(q.key)
      for (const q of mine) if (q.status === 'queued') cancelQueuedSave(q.key)
      dirty = true
      notice = 'Your prices didn’t save. Check them and press Save again.'
      void refreshVersion()
      if (mode === 'setup' || mode === 'change') draw()
      return
    }
    // It landed, but the owner has carried on editing: take the new version and keep their newer edits.
    if (dirty) {
      void refreshVersion()
      return
    }
    justSaved = mode !== 'setup'
    mode = mode === 'setup' ? 'done' : 'summary'
    changing = null
    void load(true)
    return
  }
  if (mode === 'setup' || mode === 'change') return // questions open: leave them alone
  void load(true)
}

// A save that waited for signal can be refused or clash without the app announcing a data change, so this page
// also watches the waiting-saves list itself while it is open (Astra, 1 Oct).
onQueueChange(() => {
  if (pendingSave && window.location.hash.startsWith('#/prices')) pricesOnDataChanged()
})
